import type { CustomUserInfoDTO } from 'api';
import { getCookie } from 'api';
import { logger } from 'monitor';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

export type NativeBridgeAction =
  | 'userChange'
  | 'clearCachedPhotos'
  | 'deleteCachedPhoto'
  | 'downloadFile'
  | 'openCamera'
  | 'requestCachedPhotoBytes'
  | 'requestCachedPhotos'
  | 'requestCachedPhotosStatus'
  | 'uploadCachedPhotos';

export interface NativeBridgeAuthSnapshot {
  csrfToken: string | null;
  issuedAt: number;
  user: CustomUserInfoDTO | null;
}

export interface NativeBridgeMessage<TPayload> {
  action: NativeBridgeAction;
  auth: NativeBridgeAuthSnapshot;
  payload: TPayload;
}

export const isReactNativeWebView = (): boolean => {
  return typeof window !== 'undefined' && !!window.ReactNativeWebView;
};

export const buildNativeBridgeMessage = <TPayload>(
  action: NativeBridgeAction,
  payload: TPayload,
  options?: {
    user?: CustomUserInfoDTO | null;
  }
): NativeBridgeMessage<TPayload> => {
  return {
    action,
    auth: {
      csrfToken: getCookie('csrftoken') || null,
      issuedAt: Date.now(),
      user: options?.user ?? null,
    },
    payload,
  };
};

export const postToNativeWebView = <TPayload>(
  action: NativeBridgeAction,
  payload: TPayload,
  options?: {
    user?: CustomUserInfoDTO | null;
  }
): boolean => {
  if (!isReactNativeWebView()) {
    logger.info('Native bridge message skipped because ReactNativeWebView is unavailable', { action });
    return false;
  }

  window.ReactNativeWebView?.postMessage(JSON.stringify(buildNativeBridgeMessage(action, payload, options)));
  logger.info('Native bridge message posted', { action });
  return true;
};
